import qs from 'qs';
import { PhoneIcon } from 'app/theme/icons';
import { createI18nPrice } from 'utils/i18n';
import { getUrlUtmParams, getCtaText, replaceUrlUtmParams } from 'utils/flyerGibsUrls';
import { selectFlyerGib, selectFlyerGibButtons } from 'flux/selectors/flyerGibs.selectors';
import { selectLanguage } from 'flux/selectors/ui.selectors';
import { selectCurrentFlyerPhoneFromStore, selectCurrentFlyerNearestStore, selectFlyerGibsChildIdsMap } from './flyers.selectors';
import { selectCtaTextOverride, selectCtaUtmParamsReplacementEnabled } from './property.selectors';
import { selectOriginURL } from './ui.selectors';

export const selectQuery = (state) => state.tags.query;
export const selectVisibleTagsId = (state) => state.tags.visibleTagsId;

export const selectTagButtons = (state, flyerGibId, ctaUtmParamsReplacementEnabled, originUrlUtmParams) => {
  const ctaTextOverride = selectCtaTextOverride(state);

  const buttons = selectFlyerGibButtons(state, flyerGibId).map((button) => {
    const { attributes } = button;
    const { target } = attributes;

    const { to } = qs.parse(attributes.href);
    const href = to || attributes.href;

    return {
      ...button,
      ...getCtaText(button, ctaTextOverride),
      attributes: {
        ...attributes,
        href: ctaUtmParamsReplacementEnabled ? replaceUrlUtmParams(href, originUrlUtmParams) : href,
        target: target || '_blank',
      },
    };
  });

  const { label } = selectCurrentFlyerPhoneFromStore(state);
  if (label) {
    const nearestStore = selectCurrentFlyerNearestStore(state);
    if (nearestStore?.phone) {
      buttons.push({
        elementName: 'a',
        value: label,
        Icon: PhoneIcon,
        data: {
          storeId: nearestStore?.id,
        },
        attributes: {
          href: `tel:${nearestStore?.phone}`,
          target: '_top',
        },
        name: 'btn_flyer_call',
      });
    }
  }

  return buttons;
};

// TODO consider memoizing instead of computing every time
export const selectTag = (state, flyerGibId) => {
  const flyerGib = selectFlyerGib(state, flyerGibId);
  if (!flyerGib) return null;

  const {
    id,
    parent_id: parentId,
    title,
    description,
    flyer_id: flyerId,
    product,
    settings: {
      image_url: image,
      video_url: video = '',
      price_extended: { digits: priceDigits, currency_symbol: currencySymbol } = {},
      starting_price: { digits: startingPriceDigits } = {},
      sale,
      external_url: externalUrl,
      customer_product_id,
    },
  } = flyerGib;

  const locale = selectLanguage(state);

  const ctaUtmParamsReplacementEnabled = selectCtaUtmParamsReplacementEnabled(state);
  const originURL = selectOriginURL(state);
  const { urlUtmParams: originUrlUtmParams } = getUrlUtmParams(originURL);

  const tag = {
    id,
    parentId,
    title,
    description: description && description.replace(/\n/, '<br/>'),
    image,
    video,
    price: priceDigits ? `${currencySymbol} ${createI18nPrice(locale, priceDigits)}` : null,
    initialPrice: startingPriceDigits ? `${currencySymbol} ${createI18nPrice(locale, startingPriceDigits)}` : null,
    sale,
    buttons: selectTagButtons(state, flyerGibId, ctaUtmParamsReplacementEnabled, originUrlUtmParams),
    externalUrl: ctaUtmParamsReplacementEnabled ? replaceUrlUtmParams(externalUrl, originUrlUtmParams) : externalUrl,
    flyerId,
    product: product ?? {},
    customerProductId: customer_product_id,
  };

  return tag;
};

export const selectSelectedVariantIndex = (state) => state.tags.selectedVariantIndex || 0;

export const selectProductVariantIdsInfo = (state, parentTagId) => {
  const selectedVariantIndex = selectSelectedVariantIndex(state);

  const flyerGibsChildIdsMap = selectFlyerGibsChildIdsMap(state);
  const parentChildIds = flyerGibsChildIdsMap?.[parentTagId] || [];
  const selectedTagId = parentChildIds.length === 0 ? parentTagId : parentChildIds[selectedVariantIndex];

  return {
    parentChildIds,
    selectedVariantIndex,
    selectedTagId,
  };
};

export const selectParentTagOrSelectedVariant = (state, parentTagId) => {
  const { parentChildIds, selectedVariantIndex, selectedTagId } = selectProductVariantIdsInfo(state, parentTagId);

  return {
    parentTagId,
    parentChildIds,
    selectedVariantIndex,
    selectedTag: selectTag(state, selectedTagId),
  };
};
